import React, { useCallback, useEffect, useRef, useState } from 'react';

interface UseShortFormPlaybackProps {
  shortFormPlayback: boolean;
  isVideoPlay: boolean;
  children?: React.ReactNode;
}

interface UseShortFormPlaybackReturn {
  shouldObserve: boolean;
  activeVideoIndex: number | null;
  setActiveVideoIndex: (index: number | null) => void;
  visibleIndicesRef: React.RefObject<number[]>;
  handleTouchStart: () => void;
  handleTouchEnd: () => void;
}

export function useShortFormPlayback({
  shortFormPlayback,
  isVideoPlay,
  children,
}: UseShortFormPlaybackProps): UseShortFormPlaybackReturn {
  const [shouldObserve, setShouldObserve] = useState(shortFormPlayback);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const visibleIndicesRef = useRef<number[]>([]);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const childrenKeysRef = useRef<string[]>([]);

  // 영상중지 상태
  useEffect(() => {
    if (!isVideoPlay) {
      setActiveVideoIndex(null);
      visibleIndicesRef.current = [];
      setShouldObserve(false);
    }
  }, [isVideoPlay]);

  // children이 변경될 때 상태 초기화
  useEffect(() => {
    if (!shortFormPlayback) return;

    const currentKeys = Array.isArray(children)
      ? children
          .map(child => {
            // React 엘리먼트인 경우
            if (
              React.isValidElement(child as React.ReactElement) &&
              child.props
            ) {
              return (child.props as { itemCd?: string })?.itemCd;
            }
            // Product 데이터 객체인 경우 (VirtualizedList에서 전달)
            if (child && typeof child === 'object' && 'itemCd' in child) {
              return (child as { itemCd: string })?.itemCd;
            }
            return undefined;
          })
          .filter((key): key is string => key !== undefined)
      : [];

    // 키가 변경되었다면 상태 초기화
    if (
      JSON.stringify(currentKeys) !== JSON.stringify(childrenKeysRef.current)
    ) {
      childrenKeysRef.current = currentKeys;
      setActiveVideoIndex(null);
      visibleIndicesRef.current = [];
      setShouldObserve(false);

      // 잠시 후 관찰 재시작
      if (shortFormPlayback && isVideoPlay) {
        setTimeout(() => {
          setShouldObserve(true);
        }, 100);
      }
    }
  }, [children, shortFormPlayback, isVideoPlay]);

  // shortFormPlayback이 true일 때 일정 시간 후에 첫 번째 비디오 활성화
  useEffect(() => {
    if (!isVideoPlay) return;

    if (shortFormPlayback) {
      const timer = setTimeout(() => {
        setShouldObserve(true);
        // 약간의 지연을 두고 첫 번째 비디오 활성화
        setTimeout(() => {
          if (visibleIndicesRef.current.length > 0) {
            setActiveVideoIndex(visibleIndicesRef.current[0]);
          }
        }, 200);
      }, 700);

      // eslint-disable-next-line consistent-return
      return () => clearTimeout(timer);
    }
  }, [isVideoPlay, shortFormPlayback]);

  const handleScroll = useCallback(() => {
    if (!isVideoPlay) return;

    setIsScrolling(true);
    setShouldObserve(false);

    // 이전 타이머 클리어
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    visibleIndicesRef.current = [];

    // 스크롤이 300ms 동안 멈추면 탐색 시작
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      // Observer 재시작
      setShouldObserve(true);
      // 보이는 첫 번째 비디오 재생
      setTimeout(() => {
        if (visibleIndicesRef.current.length > 0) {
          setActiveVideoIndex(visibleIndicesRef.current[0]);
        }
      }, 300);
    }, 300);
  }, [isVideoPlay]);

  const handlePageHide = useCallback(() => {
    setActiveVideoIndex(null);
  }, []);

  // pagehide, pageshow
  useEffect(() => {
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handleScroll);
    return () => {
      window.removeEventListener('pageshow', handleScroll);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [handlePageHide, handleScroll]);

  // 영상재생중 스크롤
  useEffect(() => {
    if (!isVideoPlay) return;

    if (shortFormPlayback) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      setActiveVideoIndex(null);
    };
  }, [shortFormPlayback, handleScroll, isVideoPlay]);

  const handleTouchStart = useCallback(() => {
    setShouldObserve(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (isScrolling) return;

    // 현재 활성화된 비디오 인덱스 초기화
    setActiveVideoIndex(null);
    // 보이는 인덱스 배열 초기화
    visibleIndicesRef.current = [];

    setTimeout(() => {
      // Observer 재시작
      setShouldObserve(true);

      // 약간의 지연 후 첫 번째 보이는 비디오 재생 시작
      setTimeout(() => {
        if (visibleIndicesRef.current.length > 0) {
          setActiveVideoIndex(visibleIndicesRef.current[0]);
        }
      }, 300);
    }, 100);
  }, [isScrolling]);

  return {
    shouldObserve,
    activeVideoIndex,
    setActiveVideoIndex,
    visibleIndicesRef,
    handleTouchStart,
    handleTouchEnd,
  };
}
